import React, { useEffect, useMemo, useState } from 'react';
import { message, Tooltip } from 'antd';
import styled from 'styled-components';

import type { EpiComposition, EpiDocumentType } from '../types/types';
import ActionMenu, { ActionMenuItem } from '../../ActionMenu';
import { useEpiContext } from '../context/EpiContext';
import { documentTypeName, visibleIdmp } from '../utils/schemaUtils';
import { resolveInstanceName, text } from '../utils/epiHelpers';
import TitleModal from './TitleModal';
import PeelIdmpsModal from './PeelIdmpsModal';
import MergeCompositionModal from './MergeCompositionModal';

interface IProps {
  composition: EpiComposition;
  docType: EpiDocumentType;
  showRename: () => void;
  hideRename: () => void;
  renameOpen: boolean;
}

const EpiMenuHeader: React.FC<IProps> = ({
  composition,
  docType,
  showRename,
  hideRename,
  renameOpen,
}) => {
  const {
    lang,
    readOnly,
    canManageDocuments,
    canRenameDocuments,
    visibleCompositions,
    idmpById,
    setSelectedComposition,
    peelIntoNewComposition,
    renameComposition,
    mergeCompositionIntoSibling,
  } = useEpiContext();

  const isAnnex2 = docType === 'annexII';
  const typeLabel = documentTypeName(docType);
  const title = text(composition?.title, lang);
  const [tempTitle, setTempTitle] = useState(title);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [peelMode, setPeelMode] = useState<'duplicate' | 'fresh' | null>(null);

  useEffect(() => {
    setTempTitle(title);
  }, [title]);

  const coveredIdmp = useMemo(
    () => visibleIdmp(composition?.medicinalProducts ?? []),
    [composition?.medicinalProducts]
  );

  const idmpNames = useMemo(
    () =>
      coveredIdmp.map(
        (ref, i) =>
          resolveInstanceName(idmpById[ref.id]?.data?.name, lang) ??
          `Medicinal product ${i + 1}`
      ),
    [coveredIdmp, idmpById, lang]
  );

  // Peeling needs at least two IDMPs here: one to move, one to stay behind.
  // The ePI-wide count is the wrong gate — a sibling composition may already
  // hold the others.
  const canPeel = canManageDocuments && coveredIdmp.length >= 2;
  // Merging needs somewhere to merge into.
  const canMerge = canManageDocuments && visibleCompositions.length > 1;
  // With nothing left to offer, the menu is not rendered at all.
  const showMenu = canPeel || canMerge || canRenameDocuments;

  const selectSelf = () =>
    composition?.id && setSelectedComposition(composition.id);

  const onRenameClick = () => {
    selectSelf();
    showRename();
  };

  const onRenameOk = () => {
    const trim = tempTitle.trim();
    if (!trim) return message.error('The title cannot be empty.');
    if (trim !== title) renameComposition(trim);
    hideRename();
  };

  const onPeelConfirm = (peeledIdmpUuids: string[]) => {
    if (!composition?.id || !peelMode) return;
    peelIntoNewComposition(composition.id, peeledIdmpUuids, peelMode);
    setPeelMode(null);
  };

  const onMergeConfirm = (targetId: string) => {
    if (!composition?.id || !targetId) return;
    mergeCompositionIntoSibling(composition.id, targetId);
    setMergeOpen(false);
  };

  const mergeSiblings = useMemo(
    () => visibleCompositions.filter(c => c.id !== composition?.id),
    [visibleCompositions, composition?.id]
  );

  const openPeelModal = (mode: 'duplicate' | 'fresh') => {
    selectSelf();
    setPeelMode(mode);
  };

  return (
    <>
      <Header>
        <TitleRow onClick={selectSelf}>
          <Title>{title}</Title>
          {idmpNames.length > 0 && (
            <Tooltip
              title={
                <ProductNames>
                  {idmpNames.map((name, i) => (
                    <li key={`${name}-${i}`}>{name}</li>
                  ))}
                </ProductNames>
              }
            >
              <Chip>
                {idmpNames.length}{' '}
                {idmpNames.length === 1 ? 'product' : 'products'}
              </Chip>
            </Tooltip>
          )}
        </TitleRow>
        {!isAnnex2 && !readOnly && showMenu && (
          <MenuContainer>
            <ActionMenu>
              {canPeel && (
                <ActionMenuItem onClick={() => openPeelModal('duplicate')}>
                  Duplicate {title}
                </ActionMenuItem>
              )}
              {canRenameDocuments && (
                <ActionMenuItem onClick={onRenameClick}>
                  Rename {title}
                </ActionMenuItem>
              )}
              {canPeel && (
                <ActionMenuItem onClick={() => openPeelModal('fresh')}>
                  Add new {typeLabel} document
                </ActionMenuItem>
              )}
              {canMerge && (
                <ActionMenuItem onClick={() => setMergeOpen(true)}>
                  Remove {title}
                </ActionMenuItem>
              )}
            </ActionMenu>
          </MenuContainer>
        )}
      </Header>

      <TitleModal
        key={`${composition?.id}-${renameOpen}`}
        isRenameModalVisible={renameOpen}
        handleRenameOk={onRenameOk}
        hideRenameModal={hideRename}
        tempTitle={tempTitle}
        setTempTitle={setTempTitle}
        title={title}
      />

      {mergeOpen && composition && (
        <MergeCompositionModal
          isOpen
          source={composition}
          siblings={mergeSiblings}
          onConfirm={onMergeConfirm}
          onCancel={() => setMergeOpen(false)}
        />
      )}

      {peelMode && (
        <PeelIdmpsModal
          isOpen
          mode={peelMode}
          source={composition}
          idmpById={idmpById}
          onConfirm={onPeelConfirm}
          onCancel={() => setPeelMode(null)}
        />
      )}
    </>
  );
};

export default EpiMenuHeader;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  cursor: pointer;
  /* Room for the expand arrow and the ... menu, both absolutely placed. */
  padding-right: 76px;
`;

const Title = styled.span`
  cursor: pointer;
`;

/**
 * How many products this document covers, not which — a composition can hold a
 * dozen, and naming each one buried the document's own title. The names are a
 * hover away. Light-on-dark: it sits on the dark collapse bar.
 */
const Chip = styled.span`
  white-space: nowrap;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  padding: 1px 6px;
  border-radius: 4px;
  color: #e6f4ff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
`;

const ProductNames = styled.ul`
  margin: 0;
  padding-left: 16px;
`;

const MenuContainer = styled.div`
  position: absolute;
  right: 0;
`;
